import type { YearSummary } from '../../../../entities/summary/index.js';
import {
  formatMoneyMaybeApproximate,
  formatShare,
  spentMinorOf,
} from '../../../../features/summary-view/index.js';
import {
  isPlannedApproximate,
  isSpendingApproximate,
  isSpentApproximate,
  isSubscriptionsApproximate,
} from '../../lib/spending-approximation.js';
import { spendingLabelOf } from '../../lib/spending-label.js';
import { FigureCard, FigureNote } from '../figure-card/figure-card.js';
import styles from './year-figures.module.css';

type YearFiguresProps = {
  summary: YearSummary;
  isOverview?: boolean;
};

const subscriptionsShareOf = (subscriptionsMinor: number, spendingMinor: number): string => {
  const hasShare = subscriptionsMinor > 0 && spendingMinor > 0;

  return hasShare ? ` · ${formatShare(subscriptionsMinor / spendingMinor)}` : '';
};

export const YearFigures = ({ summary, isOverview = false }: YearFiguresProps) => {
  const currency = summary.baseCurrency;
  const { spending, subscriptions, income } = summary;
  const hasBank = summary.bank !== null;
  const subscriptionsMinor = spentMinorOf(subscriptions);
  const hasSubscriptionSpending = subscriptionsMinor > 0;
  const showsSubscriptionsNote = hasBank && (isOverview || hasSubscriptionSpending);
  const showsIncomeNote = hasBank && isOverview;
  const hasPlanned = spending.plannedMinor > 0;

  return (
    <div className={styles.figures}>
      <FigureCard
        label={spendingLabelOf(summary)}
        value={formatMoneyMaybeApproximate(
          spending.totalMinor,
          currency,
          isSpendingApproximate(summary),
        )}
      >
        {hasPlanned && (
          <>
            <FigureNote>
              Уже витрачено:{' '}
              {formatMoneyMaybeApproximate(
                spending.spentMinor,
                currency,
                isSpentApproximate(summary),
              )}
            </FigureNote>
            <FigureNote>
              Ще заплановано:{' '}
              {formatMoneyMaybeApproximate(
                spending.plannedMinor,
                currency,
                isPlannedApproximate(summary),
              )}
            </FigureNote>
          </>
        )}
        {showsSubscriptionsNote && (
          <FigureNote>
            З них підписки:{' '}
            {formatMoneyMaybeApproximate(
              subscriptionsMinor,
              currency,
              isSubscriptionsApproximate(summary),
            )}
            {subscriptionsShareOf(subscriptionsMinor, spending.totalMinor)}
          </FigureNote>
        )}
        {showsIncomeNote && (
          <FigureNote>
            Надходження:{' '}
            {formatMoneyMaybeApproximate(income.totalMinor, currency, income.isConverted)}
          </FigureNote>
        )}
      </FigureCard>
    </div>
  );
};
